import React from "react";
import {
  Box,
  Paper,
  Stack,
  TablePagination,
  Theme,
  useTheme,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { SortType } from "../../../../../models/SortType";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import { PurhcaseRequestDetails } from "../../../../../models/PurchaseRequest/PurchaseRequestDetails";
import { PurchaseRequestRejectedBy } from "../../../../../models/PurchaseRequest/PurchaseRequestBody";
import {
  getModernPageColors,
  modernEmptyStateSx,
} from "../../../../../styles/modernListingPageSx";
import { useNotification } from "../../../../../utils/NotificationsContext";
import PrListCard from "./PrListCard";

interface PrListingTableProps {
  data: PurhcaseRequestDetails[];
  maxHeight?: number;
  minHeight?: number;
  limit: number;
  page_no: number;
  total_pages: number;
  total_count: number;
  sortBy?: string;
  sortType?: SortType;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number,
  ) => void;
  onRowsPerPageChange: (limit: number) => void;
  onSortChange?: (column: string, sortType: SortType) => void;
  onApprove: (prId: number, team: PurchaseRequestRejectedBy) => void;
  onReject: (prId: number, team: PurchaseRequestRejectedBy) => void;
  onGeneratePO: (prId: number) => void;
  onCancelPR: (prId: number) => void;
}

const PrListingTable = ({
  data,
  maxHeight,
  page_no,
  limit,
  total_count,
  sortBy,
  sortType,
  onPageChange,
  onRowsPerPageChange,
  onSortChange,
}: PrListingTableProps) => {
  const theme = useTheme<Theme>();
  const colors = getModernPageColors(theme);
  const navigate = useNavigate();
  const { showError } = useNotification();

  const sortOptions = [
    { key: "id", label: "PR ID" },
    { key: "order_id", label: "Order ID" },
    { key: "created_date", label: "Created Date" },
  ];

  const fieldGridSize = { xs: 4, sm: 3, md: 2, lg: 2, xl: 2 };

  const handleChangePage = (_event: unknown, newPage: number) => {
    onPageChange(null, newPage + 1);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    onPageChange(null, 1);
    onRowsPerPageChange(parseInt(event.target.value, 10));
  };

  const openPrDetails = (item: PurhcaseRequestDetails) => {
    try {
      navigate(
        `/purchase/requests/details?prId=${item.id}&projectId=${item.order_id}`,
      );
    } catch {
      showError("Error", "Failed to open purchase request details");
    }
  };

  const openOrder = (orderId: number) => {
    try {
      window.open(
        `/orders/add-orders?orderId=${orderId}`,
        "_blank",
        "noopener,noreferrer",
      );
    } catch (error) {
      showError("Error", "Failed to open order");
    }
  };

  const handleCopyText = async (
    event: React.MouseEvent,
    value?: string | number | null,
    label = "Value",
  ) => {
    event.stopPropagation();
    const text =
      value === undefined || value === null ? "" : String(value).trim();

    if (!text || text === "-") {
      showError("Copy failed", `Nothing to copy for ${label.toLowerCase()}`);
      return;
    }

    try {
      await navigator.clipboard.writeText(text);
    } catch {
      showError("Error", `Failed to copy ${label}`);
    }
  };

  const renderMeta = (label: string, value?: React.ReactNode) => (
    <Box sx={{ minWidth: 0, height: "100%" }}>
      <CustomTypography
        variant="body3"
        color={colors.subtitle}
        fontWeight={600}
        sx={{ display: "block", mb: 0.25 }}
      >
        {label}
      </CustomTypography>
      <Box
        sx={{
          minHeight: 24,
          display: "flex",
          alignItems: "center",
        }}
      >
        {typeof value === "string" || typeof value === "number" || !value ? (
          <CustomTypography
            variant="body2"
            color={colors.title}
            fontWeight={500}
            sx={{
              fontSize: "0.78rem",
              lineHeight: 1.35,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              width: "100%",
            }}
          >
            {value || "-"}
          </CustomTypography>
        ) : (
          value
        )}
      </Box>
    </Box>
  );

  return (
    <Paper
      sx={{
        borderRadius: "16px",
        border: `1px solid ${colors.border}`,
        boxShadow: colors.shadowSoft,
        overflow: "hidden",
        backgroundColor: colors.surface,
      }}
    >
      <Box
        sx={{
          px: { xs: 1.5, sm: 2 },
          py: { xs: 1.5, sm: 2 },
          borderBottom: `1px solid ${colors.border}`,
          backgroundColor: colors.surfaceMuted,
        }}
      >
        <Stack
          direction={{ xs: "column", lg: "row" }}
          justifyContent="space-between"
          spacing={1.5}
        >
          <Box>
            <CustomTypography variant="heading7" fontWeight={700}>
              Purchase Requests
            </CustomTypography>
            <CustomTypography variant="caption" color={colors.subtitle}>
              {total_count} total requests
            </CustomTypography>
          </Box>
          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            flexWrap="wrap"
            alignItems="center"
          >
            <CustomTypography variant="caption" color={colors.subtitle}>
              Sort by
            </CustomTypography>
            {sortOptions.map((option) => {
              const isActive = sortBy === option.key;
              const directionLabel =
                isActive && sortType === SortType.ASC ? "Asc" : "Desc";

              return (
                <AppButton
                  key={option.key}
                  label={
                    isActive
                      ? `${option.label}: ${directionLabel}`
                      : option.label
                  }
                  onClick={() =>
                    onSortChange?.(
                      option.key,
                      isActive && sortType === SortType.DESC
                        ? SortType.ASC
                        : SortType.DESC,
                    )
                  }
                  customStyles={{
                    minWidth: "fit-content",
                    height: 34,
                    px: 1.5,
                    borderRadius: "999px",
                    color: isActive ? colors.surface : colors.title,
                    backgroundColor: isActive
                      ? theme.palette.primary.main
                      : colors.surface,
                    border: `1px solid ${
                      isActive ? theme.palette.primary.main : colors.border
                    }`,
                    "&:hover": {
                      backgroundColor: isActive
                        ? theme.palette.primary.dark
                        : colors.surfaceMuted,
                    },
                  }}
                />
              );
            })}
          </Stack>
        </Stack>
      </Box>

      <Box
        sx={{
          maxHeight: maxHeight || window.innerHeight - 380,
          overflowY: "auto",
          p: { xs: 1, sm: 1.5 },
          backgroundColor: colors.pageBg,
        }}
      >
        <Stack spacing={1}>
          {data?.map((item) => (
            <PrListCard
              key={item.id}
              item={item}
              onOpenDetails={openPrDetails}
              onOpenOrder={openOrder}
              onCopy={handleCopyText}
              renderMeta={renderMeta}
              fieldGridSize={fieldGridSize}
            />
          ))}
        </Stack>
      </Box>

      {data.length === 0 && (
        <Box
          sx={{
            ...modernEmptyStateSx(theme),
            p: 4,
            borderTop: `1px solid ${colors.border}`,
            backgroundColor: colors.surface,
          }}
        >
          <CustomTypography
            variant="heading6"
            fontWeight={600}
            color={colors.subtitle}
          >
            No purchase requests match the current search criteria.
          </CustomTypography>
        </Box>
      )}

      {total_count > 0 && (
        <TablePagination
          rowsPerPageOptions={[30, 50, 100, 200]}
          component="div"
          count={total_count}
          rowsPerPage={limit}
          page={page_no - 1}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            borderTop: `1px solid ${colors.border}`,
            backgroundColor: colors.surface,
            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
              {
                fontSize: "0.8rem",
                color: colors.subtitle,
              },
          }}
        />
      )}
    </Paper>
  );
};

export default observer(PrListingTable);
